import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '../shared/useDocumentTitle';
import { guestWallJustOpened } from '../features/auth/guest';
import { useAuth } from '../features/auth/context';
import GuestStatusStrip from '../features/auth/GuestStatusStrip';
import { useToast } from '../hooks/useToast';
import { fetchJourneyDetail, renameJourney, deleteJourney, copyJourney, updateJourneyDates, addJourneyStop, removeJourneyStop, replaceJourneyStop, suggestJourneyStops, StopSuggestion, StopSuggestionPick, StopSuggestionTarget, changeLegMode, setStopPlanned, setLegPlanned, setLegOverrides, setLegTicketDetails, setStopStayDetails, fetchLegOmioLink, fetchNightTrainBookingLink, shiftNightTrainDate, fetchStopBookingLink, addNightTrainNearby, NightTrainNearbyOption, LegUserOverrides, LegTicketDetails, StopStayDetails, OmioRoute } from '../features/journeys/api';
import { analytics } from '../shared/analytics';
import { getModeLabel } from '../shared/transport';
import { CityResult } from '../features/geocoding/api';
import ConfirmationDialog from '../components/ConfirmationDialog';
import NameDialog from '../components/NameDialog';
import { KebabIcons } from '../features/my-trips/components/KebabMenu';
import TripShareDialog from '../features/my-trips/components/TripShareDialog';
import ShareButton from '../features/share/ShareButton';
import { fetchSharePath } from '../features/share/api';
import ShareTag from '../features/my-trips/components/ShareTag';
import NotAvailableCard from '../features/my-trips/components/NotAvailableCard';
import { assignJourney } from '../features/my-trips/api';
import JourneyRouteMap, { RoutePreview } from '../features/maps/JourneyRouteMap';
import TripDetailNav from '../features/my-trips/components/TripDetailNav';
import TripPicker, { TripChoice } from '../features/my-trips/components/TripPicker';
import MainTag from '../features/my-trips/components/MainTag';
import InfoPills from '../features/my-trips/components/InfoPills';
import JourneyTips from '../features/my-trips/components/JourneyTips';
import JourneyDates from '../features/my-trips/components/JourneyDates';
import RouteTimeline, { fmtDayRange } from '../features/my-trips/components/RouteTimeline';
import { changeStopIds, cityOnly, computeJourneySchedule, msToIsoDay } from '../features/my-trips/utils';
import LegSheet from '../features/my-trips/components/LegSheet';
import StopSheet from '../features/my-trips/components/StopSheet';
import NightTrainsNearbyDialog from '../features/my-trips/components/NightTrainsNearbyDialog';
import ApproxNote from '../features/my-trips/components/ApproxNote';
import Tooltip from '../components/ui/Tooltip';
import { APP_HEADER_HEIGHT } from '../components/Header';
import DetailSplitLayout from '../features/my-trips/components/DetailSplitLayout';
import { useJourneyRouteMap } from '../features/my-trips/useJourneyRouteMap';

// Shared, read-focused Journey detail page (design-handoff.md C.3).
// Opened from Trip detail ("See Route N in full") and, in Phase 4, from the
// Unsorted list. Structural editing stays in the existing editor.

const JourneyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast: showToastRaw } = useToast();
  // Guest signup wall (anonymous access v1): when a gated action just opened
  // the wall dialog, the wall IS the feedback — swallow the catch-block error
  // toast ("Access token required") that would otherwise stack on top of it.
  const showToast: typeof showToastRaw = useCallback((opts) => {
    if (opts?.type === 'error' && guestWallJustOpened()) return;
    showToastRaw(opts);
  }, [showToastRaw]);

  const [journey, setJourney] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  // 404 on the initial load: deleted, or another account's journey (the API
  // returns the same 404 for both). Gets the NotAvailableCard, no error toast.
  const [notFound, setNotFound] = useState(false);
  const [, setSearchParams] = useSearchParams();
  // Anonymous access: under RequireAuthOrGuest, no user means a guest session.
  const { user } = useAuth();
  const isGuest = !user;

  // Tab/history title: the route's name once loaded (see shared/useDocumentTitle).
  useDocumentTitle(journey?.title);

  // The top block (trip control + title) is sticky right under the app header
  // (APP_HEADER_HEIGHT). Measure its height so the route's nights-wheel bar can
  // pin flush below it (second sticky band).
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setHeaderH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [journey]);

  // initialView marks the page-mount fetch so the backend fires the
  // journey_detail_viewed activation event; refreshes (trip reassign, polling)
  // leave it off so they don't inflate the metric.
  const load = useCallback(async (opts?: { initialView?: boolean }) => {
    if (!id) return;
    try {
      setJourney(await fetchJourneyDetail(id, { initialView: opts?.initialView }));
    } catch (e: any) {
      // 404 is a normal outcome (wrong account, deleted, mistyped URL): the
      // page renders the explanation card — a toast on top would be noise
      // (and StrictMode's double effect used to fire it twice). A 401 whose
      // handling the auth interceptor didn't already take over (e.g. a stale
      // guest token that was just cleared) gets the same card: to this
      // browser the journey is equally unavailable.
      const status = e?.response?.status;
      if (status === 404 || (status === 401 && !guestWallJustOpened())) {
        // Someone else's journey: if its trip is shared, open the shared view
        // instead of a wall (Paulo 2026-10-03). Signed-out visitors are sent
        // there earlier, by RequireAuthOrGuest.
        const sharePath = await fetchSharePath({ journeyId: id });
        if (sharePath) {
          navigate(sharePath + window.location.search, { replace: true });
          return;
        }
        setNotFound(true);
      } else showToast({ title: 'Error', message: 'Failed to load this journey.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showToast]);

  // initialView only on the FIRST mount fetch per journey id: React StrictMode
  // (dev) runs this effect twice, which double-fired journey_detail_viewed
  // (seen in the 2026-07-29 Mixpanel export). The ref survives the StrictMode
  // remount, so the second run loads without the view flag.
  const viewedJourneyRef = useRef<string | null>(null);
  useEffect(() => {
    const firstViewOfThisJourney = viewedJourneyRef.current !== id;
    viewedJourneyRef.current = id ?? null;
    load({ initialView: firstViewOfThisJourney });
  }, [load, id]);

  // Leg prices AND stop descriptions are filled in asynchronously (PRD §4.5/§4.6).
  // While any leg lacks a price band, or any stop lacks a description, poll the
  // journey so they fill in once the backend finishes. Capped so we don't poll
  // forever if a call fails.
  const pricingPending = useMemo(
    () => (journey?.legs || []).some((l: any) => l.priceBandLowCents == null || l.priceBandHighCents == null),
    [journey]
  );
  const detailsPending = useMemo(
    () => (journey?.stops || []).some((s: any) => !s.description && s.city && s.city !== 'Unknown'),
    [journey]
  );
  // Nights per stop are suggested in the background after generation
  // (backend nightsSuggestionService); the API flags it while it runs.
  const nightsPending = Boolean(journey?.nightsPending);
  const polling = pricingPending || detailsPending || nightsPending;
  useEffect(() => {
    if (!polling || !id) return;
    let attempts = 0;
    const MAX_ATTEMPTS = 12; // ~1 min at 5s intervals
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const fresh = await fetchJourneyDetail(id);
        setJourney(fresh);
        const stillPending = (fresh?.legs || []).some((l: any) => l.priceBandLowCents == null || l.priceBandHighCents == null)
          || (fresh?.stops || []).some((s: any) => !s.description && s.city && s.city !== 'Unknown')
          || Boolean(fresh?.nightsPending);
        if (!stillPending || attempts >= MAX_ATTEMPTS) clearInterval(timer);
      } catch {
        if (attempts >= MAX_ATTEMPTS) clearInterval(timer);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [polling, id]);

  // Inline native editor (deterministic): edit affordances live on each stop/leg
  // (hover → pencil/trash); each action hits a targeted endpoint that returns the
  // updated journey, which we set directly (server recomputes the affected legs +
  // re-prices them). `mutating` disables controls while a change is in flight.
  const [mutating, setMutating] = useState(false);
  // Destructive structural edits go through the shared ConfirmationDialog (same
  // convention as the legacy journey delete) before running.
  const [confirm, setConfirm] = useState<null | { title: string; message: string; confirmText: string; onConfirm: () => void }>(null);
  const runEdit = useCallback(async (fn: () => Promise<any>) => {
    setMutating(true);
    try {
      setJourney(await fn());
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not apply the change. Please try again.', type: 'error' });
    } finally {
      setMutating(false);
    }
  }, [showToast]);
  // Structural edits recreate the adjacent legs, which DROPS any user-entered
  // cost/time values on them (userOverrides). When that's about to happen the
  // confirmation names the affected legs so the loss is explicit.
  const overriddenLegsNote = useCallback((affected: any[]): string => {
    const byId = (sid: string) => (journey?.stops || []).find((s: any) => s.id === sid);
    const withOv = affected.filter((l: any) => l.userOverrides && Object.keys(l.userOverrides).length > 0);
    if (withOv.length === 0) return '';
    const names = withOv.map((l: any) => `${byId(l.fromStopId)?.city || '?'} → ${byId(l.toStopId)?.city || '?'}`).join(', ');
    return ` This will recalculate the ${names} leg${withOv.length > 1 ? 's' : ''}, where you entered your own values. Those entries will be lost.`;
  }, [journey]);
  // A stop's stay (place name / price / the planned flag) belongs to the PLACE,
  // so replacing or removing the stop drops it. Name it in the confirmation so
  // the loss is never a surprise — the backend clears it on replace.
  const stayDetailsNote = useCallback((stopId: string): string => {
    const stop = (journey?.stops || []).find((s: any) => s.id === stopId);
    if (!stop) return '';
    const stay = stop.stayDetails || null;
    const hasStay = !!(stay && (stay.placeName || stay.costCents != null)) || !!stop.planned;
    if (!hasStay) return '';
    const what = stay?.placeName ? `the stay you saved (${stay.placeName})` : 'the stay details you saved';
    return ` This also removes ${what}.`;
  }, [journey]);

  const handleAddStop = useCallback((afterStopId: string | null, city: CityResult, suggestion?: StopSuggestionPick) => {
    if (!journey) return;
    const run = () => runEdit(() => addJourneyStop(journey.id, { afterStopId, city: city.name, country: city.country, latitude: city.lat, longitude: city.lng, suggestion }));
    // Adding inside the route splits the leg that starts at `afterStopId`; only
    // ask first when that split loses user-entered values.
    const split = afterStopId ? (journey.legs || []).filter((l: any) => l.fromStopId === afterStopId) : [];
    const note = overriddenLegsNote(split);
    if (!note) return run();
    setConfirm({
      title: 'Add stop',
      message: `Add ${city.name} here?${note}`,
      confirmText: 'Add',
      onConfirm: run,
    });
  }, [journey, runEdit, overriddenLegsNote]);
  const handleRemoveStop = useCallback((stopId: string) => {
    if (!journey) return;
    const adjacent = (journey.legs || []).filter((l: any) => l.fromStopId === stopId || l.toStopId === stopId);
    setConfirm({
      title: 'Remove stop',
      message: `Remove this stop from your journey?${stayDetailsNote(stopId)}${overriddenLegsNote(adjacent)}`,
      confirmText: 'Remove',
      onConfirm: () => runEdit(() => removeJourneyStop(journey.id, stopId)),
    });
  }, [journey, runEdit, overriddenLegsNote, stayDetailsNote]);
  const handleReplaceStop = useCallback((stopId: string, city: CityResult, suggestion?: StopSuggestionPick) => {
    if (!journey) return;
    const adjacent = (journey.legs || []).filter((l: any) => l.fromStopId === stopId || l.toStopId === stopId);
    setConfirm({
      title: 'Replace stop',
      message: `Replace this stop with ${city.name}?${stayDetailsNote(stopId)}${overriddenLegsNote(adjacent)}`,
      confirmText: 'Replace',
      onConfirm: () => runEdit(() => replaceJourneyStop(journey.id, stopId, { city: city.name, country: city.country, latitude: city.lat, longitude: city.lng, suggestion })),
    });
  }, [journey, runEdit, overriddenLegsNote, stayDetailsNote]);
  // "Describe a place" in the add/replace picker (StopPlacePicker): read-only,
  // the pick then runs through handleAddStop / handleReplaceStop above.
  const journeyId = journey?.id;
  const handleSuggestPlaces = useCallback((target: StopSuggestionTarget, instructions: string) => {
    if (!journeyId) return Promise.reject(new Error('No journey'));
    return suggestJourneyStops(journeyId, target, instructions);
  }, [journeyId]);
  // Hovering a suggested place shows it on the map, between the stops it
  // would sit between (JourneyRouteMap's RoutePreview).
  const [placePreview, setPlacePreview] = useState<{ target: StopSuggestionTarget; place: StopSuggestion } | null>(null);
  const handlePreviewPlace = useCallback((target: StopSuggestionTarget, place: StopSuggestion | null) => {
    setPlacePreview(place ? { target, place } : null);
  }, []);
  // Leg detail sheet (LegSheet): opened by tapping a leg row; edits (transport
  // mode, planned) are drafted in the sheet and committed here on Save. A mode
  // change goes through the deterministic recompute + async re-price; the
  // planned flag is a plain persist. Returns success so the sheet knows to close.
  const [openLegId, setOpenLegId] = useState<string | null>(null);
  // The one client-side event of the 2026-07 instrumentation pass: opening the
  // sheet triggers no API call, so there is no server hook to fire it from.
  const handleOpenLeg = useCallback((legId: string, source: 'card' | 'pill' = 'card') => {
    setOpenLegId(legId);
    const leg = (journey?.legs || []).find((l: any) => l.id === legId);
    if (journey && leg) {
      analytics.trackLegDetailsViewed({
        journey_id: journey.id,
        leg_id: legId,
        transport_mode: leg.transportMode || null,
        planned: Boolean(leg.planned),
        source: source === 'pill' ? 'pill_tap' : 'card_tap',
      });
    }
  }, [journey]);
  const openLeg = useMemo(() => (journey?.legs || []).find((l: any) => l.id === openLegId) || null, [journey, openLegId]);
  const openLegStops = useMemo(() => {
    if (!openLeg) return { from: '', to: '' };
    const byId = (id: string) => (journey?.stops || []).find((s: any) => s.id === id);
    return { from: byId(openLeg.fromStopId)?.city || '', to: byId(openLeg.toStopId)?.city || '' };
  }, [journey, openLeg]);
  // Changes: stops with 0 nights between two others. Only the night-train
  // sheet says so ("Change in …"); the stops themselves look like any other.
  const changeIds = useMemo(() => changeStopIds(journey?.stops || []), [journey]);
  // Per-leg Omio affiliate CTA on the timeline (shortest path to a booking
  // click; the leg sheet has its own copy of this flow). The blank window is
  // opened synchronously so popup blockers allow it.
  const [omioBusyLegId, setOmioBusyLegId] = useState<string | null>(null);
  // The leg's expected travel day (schedule walk over the saved nights;
  // user-set ticket dates re-anchor it). Sent as the Omio deep-link `date`
  // and, as a day count, as event context for affiliate_link_clicked.
  const legTravelDay = useCallback((legId: string): { date: string | null; daysUntilDeparture: number | null } => {
    if (!journey) return { date: null, daysUntilDeparture: null };
    const day = computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).legDays[legId];
    if (day == null || !Number.isFinite(day.ms)) return { date: null, daysUntilDeparture: null };
    const now = new Date();
    const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    return { date: msToIsoDay(day.ms), daysUntilDeparture: Math.round((day.ms - todayUtc) / 86400000) };
  }, [journey]);
  // The leg's two stops (city + country): the Omio link pre-fills Omio's
  // search by position id, looked up in the browser at the click
  // (features/journeys/omioPositions.ts).
  const legRoute = useCallback((legId: string): OmioRoute | null => {
    const leg = (journey?.legs || []).find((l: any) => l.id === legId);
    if (!leg) return null;
    const stop = (id: string) => (journey?.stops || []).find((s: any) => s.id === id);
    const from = stop(leg.fromStopId);
    const to = stop(leg.toStopId);
    return { from: { city: from?.city ?? null, country: from?.country ?? null }, to: { city: to?.city ?? null, country: to?.country ?? null } };
  }, [journey]);
  const handleOmioLeg = useCallback((legId: string) => {
    if (!journey || omioBusyLegId) return;
    const win = window.open('', '_blank');
    setOmioBusyLegId(legId);
    const { date, daysUntilDeparture } = legTravelDay(legId);
    fetchLegOmioLink(journey.id, legId, { source: 'timeline', daysUntilDeparture, date, route: legRoute(legId) })
      .then(({ url }) => {
        if (win) win.location.href = url;
        else window.open(url, '_blank');
      })
      .catch(() => {
        if (win) win.close();
        showToast({ title: 'Could not open the ticket search', message: 'Please try again.', type: 'error', duration: 4000 });
      })
      .finally(() => setOmioBusyLegId(null));
  }, [journey, omioBusyLegId, legTravelDay, legRoute, showToast]);

  // Night-train booking CTA on the timeline: the operator's site (with Jody's
  // UTM parameters, added by the backend) or Omio, per the operator's channel.
  const handleNightTrainBooking = useCallback((legId: string) => {
    if (!journey || omioBusyLegId) return;
    const win = window.open('', '_blank');
    setOmioBusyLegId(legId);
    const { date, daysUntilDeparture } = legTravelDay(legId);
    // The Omio lookup only for a train sold on Omio (see legRoute).
    const leg = (journey.legs || []).find((l: any) => l.id === legId);
    const route = leg?.nightTrain?.preferredChannel === 'omio' ? legRoute(legId) : null;
    fetchNightTrainBookingLink(journey.id, legId, { source: 'timeline', daysUntilDeparture, date, route })
      .then(({ url }) => {
        if (win) win.location.href = url;
        else window.open(url, '_blank');
      })
      .catch(() => {
        if (win) win.close();
        showToast({ title: 'Could not open the booking page', message: 'Please try again.', type: 'error', duration: 4000 });
      })
      .finally(() => setOmioBusyLegId(null));
  }, [journey, omioBusyLegId, legTravelDay, legRoute, showToast]);

  // Night train that does not run on its date: one tap moves a night between
  // the two stops so it does (leg sheet; night trains spec §7.2).
  const handleShiftNightTrainDate = useCallback(async (legId: string, date: string): Promise<boolean> => {
    if (!journey) return false;
    setMutating(true);
    try {
      setJourney(await shiftNightTrainDate(journey.id, legId, date));
      showToast({ title: 'Saved', message: 'Your dates now match the night train.', type: 'success', duration: 3000 });
      return true;
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not change the dates. Please try again.', type: 'error' });
      return false;
    } finally {
      setMutating(false);
    }
  }, [journey, showToast]);

  // Expected travel day for the open leg (from the schedule walk over the SAVED
  // nights; the timeline's live stepper overlay doesn't need to feed the sheet).
  const openLegExpectedDate = useMemo(() => {
    if (!openLeg || !journey?.departureDate) return null;
    const day = computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).legDays[openLeg.id];
    return day != null ? msToIsoDay(day.ms) : null;
  }, [journey, openLeg]);
  const handleSaveLeg = useCallback(async (legId: string, changes: { transportMode?: string; planned?: boolean; plannedVia?: 'sheet' | 'save_dialog'; userOverrides?: LegUserOverrides | null; ticketDetails?: LegTicketDetails | null }): Promise<boolean> => {
    if (!journey) return false;
    setMutating(true);
    try {
      if (changes.transportMode !== undefined) {
        setJourney(await changeLegMode(journey.id, legId, changes.transportMode));
      }
      // Overrides go AFTER a mode change: the totals recompute inside this
      // call, so it should see the final mode.
      if (changes.userOverrides !== undefined) {
        setJourney(await setLegOverrides(journey.id, legId, changes.userOverrides));
      }
      if (changes.planned !== undefined) {
        setJourney(await setLegPlanned(journey.id, legId, changes.planned, changes.plannedVia ?? 'sheet'));
      }
      if (changes.ticketDetails !== undefined) {
        setJourney(await setLegTicketDetails(journey.id, legId, changes.ticketDetails));
      }
      // When the saved values apply to the mode this leg actually uses, say so:
      // the leg's numbers AND the journey totals just moved to the user's values.
      const finalMode = changes.transportMode
        ?? String((journey.legs || []).find((l: any) => l.id === legId)?.transportMode || 'train');
      const appliedEntry = changes.userOverrides?.[finalMode];
      const appliedToLeg = appliedEntry != null && (appliedEntry.costCents != null || appliedEntry.durationMinutes != null);
      showToast({
        title: 'Saved',
        message: appliedToLeg
          ? 'This leg now uses your values, and the journey totals were updated to match.'
          : 'Your changes were saved.',
        type: 'success',
        duration: 3000,
      });
      return true;
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not save the changes. Please try again.', type: 'error' });
      return false;
    } finally {
      setMutating(false);
    }
  }, [journey, showToast]);

  // Quick transport switch from a leg card's emoji picker. Same persistence as
  // the leg sheet's mode change, minus the sheet's draft/Save step — the picker
  // is a one-tap action, so it commits immediately. Switching is non-destructive:
  // values the user typed stay filed under their own mode in userOverrides.
  const handleChangeLegMode = useCallback(async (legId: string, mode: string) => {
    if (!journey || mutating) return;
    setMutating(true);
    try {
      setJourney(await changeLegMode(journey.id, legId, mode));
      showToast({ title: 'Transport updated', message: `This leg now goes by ${getModeLabel(mode).toLowerCase()}.`, type: 'success', duration: 3000 });
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not change the transport. Please try again.', type: 'error' });
    } finally {
      setMutating(false);
    }
  }, [journey, mutating, showToast]);

  // Stop detail sheet (StopSheet): opened by tapping a stop's name or its
  // Planned pill; stay details + the planned flag are drafted in the sheet
  // and committed here on Save. Plain persists, no recompute/re-price.
  const [openStopId, setOpenStopId] = useState<string | null>(null);
  // Sheet → "Replace": the city picker lives on the timeline row, so the sheet
  // closes and hands the row a request to enter its picker mode.
  const [replaceRequestStopId, setReplaceRequestStopId] = useState<string | null>(null);
  const handleOpenStop = useCallback((stopId: string) => setOpenStopId(stopId), []);
  // Where the route map goes and how it follows the timeline (placement,
  // hover/sheet highlight, map pick, numbered stops): the same hook as the
  // shared journey view. Tablets pin the map in the sticky header band below;
  // phones let it scroll away so the sticky nights-wheel bar gets that space.
  const routeMap = useJourneyRouteMap({ journey, openLegId, openStopId, onOpenStop: handleOpenStop, tabletHeaderMap: true });
  const mapPlacement = routeMap.placement;
  const openStop = useMemo(() => (journey?.stops || []).find((s: any) => s.id === openStopId) || null, [journey, openStopId]);
  // Expected stay window for the open stop (schedule walk over the SAVED
  // nights) — rides into the Booking.com link so the search lands prefilled.
  const openStopWindow = useMemo(() => {
    const empty = { checkin: null as string | null, checkout: null as string | null, label: null as string | null };
    if (!openStop || !journey?.departureDate) return empty;
    const w = computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).stopWindows[openStop.id];
    if (!w) return empty;
    return {
      checkin: msToIsoDay(w.fromMs),
      checkout: msToIsoDay(w.toMs),
      label: w.toMs >= w.fromMs ? fmtDayRange(w.fromMs, w.toMs) : null,
    };
  }, [journey, openStop]);

  // ── Open detail sheet ↔ URL (?leg=<id> / ?stop=<id>) ──────────────────────
  // The open sheet rides in the URL so (a) a guest hitting the signup wall
  // from inside a sheet returns to that exact sheet after signing up — the
  // wall's ?next= captures pathname+search — and (b) sheet views deep-link in
  // general. `replace` keeps sheet opens/closes out of the history stack.
  const sheetRestoredRef = useRef(false);
  useEffect(() => {
    if (!journey || sheetRestoredRef.current) return;
    sheetRestoredRef.current = true;
    const sp = new URLSearchParams(window.location.search);
    const legParam = sp.get('leg');
    const stopParam = sp.get('stop');
    if (legParam && (journey.legs || []).some((l: any) => l.id === legParam)) setOpenLegId(legParam);
    else if (stopParam && (journey.stops || []).some((s: any) => s.id === stopParam)) setOpenStopId(stopParam);
  }, [journey]);
  useEffect(() => {
    // Never strip the params before the restore above has consumed them.
    if (!sheetRestoredRef.current) return;
    const sp = new URLSearchParams(window.location.search);
    const before = sp.toString();
    sp.delete('leg');
    sp.delete('stop');
    if (openLegId) sp.set('leg', openLegId);
    else if (openStopId) sp.set('stop', openStopId);
    if (sp.toString() !== before) setSearchParams(sp, { replace: true });
  }, [openLegId, openStopId, setSearchParams]);

  // "Search stays" from a stop card or the sheet (the stop twin of
  // handleOmioLeg): blank window opened synchronously for popup blockers,
  // then the server-built Booking link — the request is the tracked click.
  const [bookingBusyStopId, setBookingBusyStopId] = useState<string | null>(null);
  const stopStayWindow = useCallback((stopId: string): { checkin: string | null; checkout: string | null } => {
    if (!journey?.departureDate) return { checkin: null, checkout: null };
    const w = computeJourneySchedule(journey.stops || [], journey.legs || [], journey.departureDate).stopWindows[stopId];
    if (!w) return { checkin: null, checkout: null };
    return { checkin: msToIsoDay(w.fromMs), checkout: msToIsoDay(w.toMs) };
  }, [journey]);
  const handleBookingStop = useCallback((stopId: string) => {
    if (!journey || bookingBusyStopId) return;
    const win = window.open('', '_blank');
    setBookingBusyStopId(stopId);
    const { checkin, checkout } = stopStayWindow(stopId);
    fetchStopBookingLink(journey.id, stopId, { checkin, checkout, source: 'timeline' })
      .then(({ url }) => {
        if (win) win.location.href = url;
        else window.open(url, '_blank');
      })
      .catch(() => {
        if (win) win.close();
        showToast({ title: 'Could not open the stay search', message: 'Please try again.', type: 'error', duration: 4000 });
      })
      .finally(() => setBookingBusyStopId(null));
  }, [journey, bookingBusyStopId, stopStayWindow, showToast]);
  // Nights changed from the SHEET's stepper (it persists itself, debounced):
  // patch the journey state so the timeline's dates, the sheet's window label
  // and the card's stepper (keyed on this value) all follow.
  const handleStopNightsChange = useCallback((stopId: string, nights: number) => {
    setJourney((j: any) => (j ? { ...j, stops: (j.stops || []).map((s: any) => (s.id === stopId ? { ...s, stopDurationHours: nights * 24 } : s)) } : j));
  }, []);
  // Once a nights change is SAVED: the nights at a stop move the dates of
  // every later part of the trip, and with them what the timetable says
  // about it (whether a night train runs that day, what the night-train
  // search would find). The backend works those out when it reads the
  // journey, so the page reads it again and takes them from there. Only
  // those facts, leg by leg, and only for a leg that is travelled the same
  // way: nights of other stops may be mid-edit, and an edit made meanwhile
  // wins.
  const nightFactsRequest = useRef(0);
  const handleStopNightsSaved = useCallback(async () => {
    if (!id) return;
    const request = ++nightFactsRequest.current;
    try {
      const fresh = await fetchJourneyDetail(id);
      if (nightFactsRequest.current !== request) return;
      const byId = new Map<string, any>((fresh?.legs || []).map((l: any) => [l.id, l]));
      setJourney((j: any) => (j ? {
        ...j,
        legs: (j.legs || []).map((l: any) => {
          const f = byId.get(l.id);
          if (!f || f.transportMode !== l.transportMode) return l;
          return {
            ...l,
            nightTrain: f.nightTrain,
            nightTrainAvailable: f.nightTrainAvailable,
            nightTrainStatus: f.nightTrainStatus,
            nightTrainNote: f.nightTrainNote,
            nightTrainSearch: f.nightTrainSearch,
            durationMinutes: f.durationMinutes,
            durationHours: f.durationHours,
          };
        }),
      } : j));
    } catch {
      // Not shown: the next read of the journey brings them.
    }
  }, [id]);
  // "Search night trains nearby" (night trains, slice 6): opened from a
  // transport picker on a leg without a direct night train. Adding the picked
  // train rewrites that part of the route, so the leg sheet closes with it.
  // ONE dialog for the whole page: the transport lists, the leg sheet, the
  // leg cards and the summary pill all ask for it through these two.
  // `legId` null: opened for the journey (the summary pill), the traveller
  // picks the part of the trip in the dialog.
  const [nearby, setNearby] = useState<{ legId: string | null } | null>(null);
  const setNearbyLegId = useCallback((legId: string | null) => setNearby({ legId }), []);
  // The parts of the trip, in order, as they are travelled today.
  const nearbyLegs = useMemo(() => {
    const byId = (id: string) => (journey?.stops || []).find((s: any) => s.id === id);
    return [...(journey?.legs || [])]
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
      .map((l: any) => ({
        id: l.id as string,
        label: `${cityOnly(byId(l.fromStopId)?.city)} → ${cityOnly(byId(l.toStopId)?.city)}`,
        transportMode: l.transportMode,
        status: l.nightTrainStatus,
        search: l.nightTrainSearch,
        booked: Boolean(l.planned),
      }));
  }, [journey]);
  const openNightTrainSearch = useCallback(() => setNearby({ legId: null }), []);
  const handleAddNightTrainNearby = useCallback(async (legId: string, option: NightTrainNearbyOption): Promise<boolean> => {
    if (!journey) return false;
    setMutating(true);
    try {
      setJourney(await addNightTrainNearby(journey.id, legId, option.train));
      setOpenLegId(null);
      showToast({ title: 'Saved', message: 'The night train was added to your trip.', type: 'success', duration: 3000 });
      return true;
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not add the night train. Please try again.', type: 'error' });
      return false;
    } finally {
      setMutating(false);
    }
  }, [journey, showToast]);

  const handleSaveStop = useCallback(async (stopId: string, changes: { planned?: boolean; stayDetails?: StopStayDetails | null }): Promise<boolean> => {
    if (!journey) return false;
    setMutating(true);
    try {
      if (changes.stayDetails !== undefined) {
        setJourney(await setStopStayDetails(journey.id, stopId, changes.stayDetails));
      }
      if (changes.planned !== undefined) {
        setJourney(await setStopPlanned(journey.id, stopId, changes.planned));
      }
      showToast({ title: 'Saved', message: 'Your stay details were saved.', type: 'success', duration: 3000 });
      return true;
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not save the changes. Please try again.', type: 'error' });
      return false;
    } finally {
      setMutating(false);
    }
  }, [journey, showToast]);

  // Planned/Unplanned status pills (prototype §7, simplified): toggle
  // optimistically for a snappy checkbox, persist in the background, and revert
  // + toast if the PATCH fails. No recompute involved, so no runEdit/mutating.
  const patchPlanned = useCallback((kind: 'stops' | 'legs', itemId: string, planned: boolean) => {
    setJourney((j: any) => (j ? { ...j, [kind]: (j[kind] || []).map((it: any) => (it.id === itemId ? { ...it, planned } : it)) } : j));
  }, []);
  const handleToggleStopPlanned = useCallback((stopId: string, planned: boolean) => {
    if (!journey) return;
    patchPlanned('stops', stopId, planned);
    setStopPlanned(journey.id, stopId, planned)
      .then(() => {
        showToast({ title: 'Saved', message: planned ? 'Stay marked as booked.' : 'Stay marked as not booked.', type: 'success', duration: 2500 });
      })
      .catch(() => {
        patchPlanned('stops', stopId, !planned);
        showToast({ title: 'Error', message: 'Could not update the status. Please try again.', type: 'error' });
      });
  }, [journey, patchPlanned, showToast]);
  const handleToggleLegPlanned = useCallback((legId: string, planned: boolean) => {
    if (!journey) return;
    patchPlanned('legs', legId, planned);
    setLegPlanned(journey.id, legId, planned)
      .then(() => {
        showToast({ title: 'Saved', message: planned ? 'Ticket marked as booked.' : 'Ticket marked as not booked.', type: 'success', duration: 2500 });
      })
      .catch(() => {
        patchPlanned('legs', legId, !planned);
        showToast({ title: 'Error', message: 'Could not update the status. Please try again.', type: 'error' });
      });
  }, [journey, patchPlanned, showToast]);

  // Edit the journey's travel dates (metadata-only). On success patch local
  // state so the dates section + leg pricing seasonality reflect the change.
  const handleSaveDates = useCallback(async (departureDate: string | null, returnDate: string | null): Promise<boolean> => {
    if (!journey) return false;
    const res = await updateJourneyDates(journey.id, departureDate, returnDate);
    if (!res.success) {
      showToast({ title: 'Error', message: res.error?.message || 'Failed to update the dates.', type: 'error' });
      return false;
    }
    setJourney((j: any) => (j ? { ...j, departureDate, returnDate } : j));
    showToast({ title: 'Saved', message: 'Travel dates updated.', type: 'success' });
    return true;
  }, [journey, showToast]);

  // Inline rename + delete of this journey (title is metadata-only; structural
  // edits stay in the editor). Rename PUTs the new title and patches local
  // state; delete confirms, removes, then returns to the parent trip (or list).
  const [renaming, setRenaming] = useState(false);
  // Trip sharing v1: the share dialog. Sharing from the journey page still
  // shares the whole PARENT trip (permission is trip-level, product decision
  // 2026-09-10), so this needs journey.tripId and is hidden for orphans. The
  // link it hands out opens THIS journey (Paulo 2026-10-03).
  const [shareOpen, setShareOpen] = useState(false);
  // Same NameDialog, pre-filled "Copy of X". From the journey page we land on
  // the copy, since you were looking at one route and now own a second.
  const [copying, setCopying] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const startRename = useCallback(() => {
    setRenaming(true);
  }, []);

  const handleCopy = useCallback(async (title: string) => {
    if (!journey) return;
    setCopyBusy(true);
    try {
      const created = await copyJourney(journey.id, title);
      setCopying(false);
      showToast({ title: 'Copied', message: `"${title}" is ready to edit.`, type: 'success', duration: 3000 });
      navigate(`/journeys/${encodeURIComponent(created.id)}`);
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || 'Could not copy this journey. Please try again.', type: 'error' });
    } finally {
      setCopyBusy(false);
    }
  }, [journey, navigate, showToast]);

  const handleRename = useCallback(async (next: string) => {
    if (!journey) return;
    if (!next || next === journey.title) { setRenaming(false); return; }
    setSavingName(true);
    try {
      const res = await renameJourney(journey.id, next);
      if (!res.success) throw new Error(res.error?.message || 'Rename failed');
      setJourney((j: any) => (j ? { ...j, title: next } : j));
      setRenaming(false);
      showToast({ title: 'Renamed', message: 'Journey name updated.', type: 'success' });
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.message || 'Failed to rename journey', type: 'error' });
    } finally {
      setSavingName(false);
    }
  }, [journey, showToast]);

  const performDelete = useCallback(async () => {
    if (!journey) return;
    setDeleting(true);
    try {
      const res = await deleteJourney(journey.id);
      if (!res.success) throw new Error(res.error?.message || 'Delete failed');
      showToast({ title: 'Deleted', message: 'Journey deleted.', type: 'success' });
      navigate(journey.tripId ? `/trips/${encodeURIComponent(journey.tripId)}` : '/trips');
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.message || 'Failed to delete journey', type: 'error' });
      setDeleting(false);
    }
  }, [journey, navigate, showToast]);

  // Kebab → Delete. Routed through the shared ConfirmationDialog rather than
  // window.confirm (ui-conventions.md §Dialogs).
  const handleDelete = useCallback(() => {
    if (!journey) return;
    setConfirm({
      title: 'Delete this journey?',
      message: 'This route and everything you saved on it will be permanently deleted.',
      confirmText: 'Delete',
      onConfirm: () => { setConfirm(null); performDelete(); },
    });
  }, [journey, performDelete]);

  // Change which trip this journey belongs to (or to a new/none). Reassign,
  // then refetch so the MAIN tag and selection reflect the new state.
  const [reassigning, setReassigning] = useState(false);
  // For journeys that already have a parent trip, the trip selector is hidden
  // behind a pencil; this toggles it open.
  const [editingTrip, setEditingTrip] = useState(false);
  const handleTripChange = useCallback(async (choice: TripChoice) => {
    if (!journey) return;
    setReassigning(true);
    try {
      await assignJourney(journey.id, choice);
      showToast({ title: 'Updated', message: 'Trip updated for this journey.', type: 'success' });
      await load();
    } catch (e: any) {
      showToast({ title: 'Error', message: e?.response?.data?.error || e.message || 'Failed to update trip', type: 'error' });
    } finally {
      setReassigning(false);
    }
  }, [journey, load, showToast]);

  const mapPreview = useMemo<RoutePreview | null>(() => {
    if (!placePreview) return null;
    const stops: any[] = [...(journey?.stops ?? [])].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
    const at = (s: any) => (s ? { lat: parseFloat(s.latitude), lng: parseFloat(s.longitude) } : null);
    const { target, place } = placePreview;
    let from = null;
    let to = null;
    if (target.action === 'replace') {
      const i = stops.findIndex((s) => s.id === target.stopId);
      from = at(stops[i - 1]);
      to = at(stops[i + 1]);
    } else if (target.afterStopId == null) {
      to = at(stops[0]);
    } else {
      const i = stops.findIndex((s) => s.id === target.afterStopId);
      from = at(stops[i]);
      to = at(stops[i + 1]);
    }
    return { lat: place.latitude, lng: place.longitude, label: place.city, from, to };
  }, [placePreview, journey]);

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%' }}>
      <DetailSplitLayout
        map={mapPlacement === 'beside' && routeMap.routeStops.length > 0 ? (
          <JourneyRouteMap
            stops={routeMap.routeStops}
            highlight={routeMap.highlight}
            preview={mapPreview}
            fill
            {...routeMap.sideMapProps}
          />
        ) : null}
      >
        {loading ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Loading journey…</div>
        ) : notFound ? (
          <NotAvailableCard kind="journey" isGuest={isGuest} />
        ) : !journey ? (
          <div style={{ color: '#6c7872', padding: '24px 0' }}>Journey not found.</div>
        ) : (
          <>
            {/* Parent-trip control. When the journey already belongs to a trip,
                show a breadcrumb back to it plus a pencil that reveals the trip
                selector only on demand (keeps the top uncluttered). For an
                orphan journey, the selector shows directly so it can be filed.
                The breadcrumb navigates with ?focus= (cross-page nav contract —
                see docs/codebase/cross-page-nav-contract.md); the label uses
                journey.tripTitle from the journey-detail payload. */}
            {/* Header band: trip control + title, pinned below the global app
                header (sticky, APP_HEADER_HEIGHT tall). Its height is measured
                so the route's nights-wheel bar can stack flush beneath it. The map
                deliberately lives OUTSIDE this band (scrolls away). */}
            {isGuest && <GuestStatusStrip />}
            <div ref={headerRef} style={{ position: 'sticky', top: APP_HEADER_HEIGHT, zIndex: 20, background: 'var(--bg)', paddingTop: 8, paddingBottom: 8 }}>
            {journey.tripId ? (
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={() => navigate(`/trips/${encodeURIComponent(journey.tripId)}?focus=${encodeURIComponent(journey.id)}`)}
                    title={`Go to trip: ${journey.tripTitle || 'Trip'}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      minWidth: 0,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      color: '#157a55',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    <span aria-hidden style={{ fontSize: 16, lineHeight: 1, marginTop: -1 }}>‹</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {journey.tripTitle ? `Trip: ${journey.tripTitle}` : 'Trip'}
                    </span>
                  </button>
                </div>
                {editingTrip && (
                  <div style={{ marginTop: 8 }}>
                    <TripPicker
                      value={journey.tripId ?? null}
                      onSelect={(choice) => { handleTripChange(choice); setEditingTrip(false); }}
                      includeCreateNew
                      newTripDefaultName={journey.title || ''}
                      firstOptionLabel="No trip (Unsorted)"
                      disabled={reassigning}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <TripPicker
                  value={null}
                  onSelect={handleTripChange}
                  includeCreateNew
                  newTripDefaultName={journey.title || ''}
                  firstOptionLabel="No trip (Unsorted)"
                  label="Add to trip"
                  disabled={reassigning}
                />
              </div>
            )}

            <TripDetailNav
              title={journey.title || 'Journey'}
              onBack={() => navigate(-1)}
              titleBadge={(journey.isMain || (journey.tripShareVisibility && journey.tripShareVisibility !== 'private')) ? (
                <>
                  {journey.isMain && <MainTag />}
                  {journey.tripShareVisibility && journey.tripShareVisibility !== 'private' && (
                    <ShareTag visibility={journey.tripShareVisibility} />
                  )}
                </>
              ) : null}
              // Desktop also shows Share as a visible button beside the ⋮,
              // which keeps its own Share row (Paulo 2026-10-03).
              actions={journey.tripId && mapPlacement === 'beside' ? (
                <ShareButton onClick={() => setShareOpen(true)} />
              ) : undefined}
              menuAriaLabel="Journey options"
              menuItems={[
                { label: 'Share trip', icon: KebabIcons.share, hidden: !journey.tripId, onSelect: () => setShareOpen(true) },
                { label: 'Rename', icon: KebabIcons.pencil, onSelect: startRename },
                { label: 'Create copy', icon: KebabIcons.copy, onSelect: () => setCopying(true) },
                { label: 'Move under another trip', icon: KebabIcons.move, onSelect: () => setEditingTrip(true) },
                { label: 'Delete', icon: KebabIcons.trash, tone: 'danger', onSelect: handleDelete },
              ]}
            />


            {/* Tablets: keep the map pinned INSIDE the sticky header band — the
                header-height measurement includes it, so the wheel bar still
                stacks flush below. Desktop draws it beside the page instead;
                phones skip this branch. */}
            {mapPlacement === 'header' && routeMap.routeStops.length > 0 && (
              <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 8 }}>
                <JourneyRouteMap stops={routeMap.routeStops} highlight={routeMap.highlight} preview={mapPreview} height={240} />
              </div>
            )}
            </div>

            {/* Phones: the map lives OUTSIDE the sticky band and scrolls away, so
                the sticky nights-wheel bar gets the pinned space. */}
            {mapPlacement === 'inline' && routeMap.routeStops.length > 0 && (
              <div style={{ borderRadius: 16, overflow: 'hidden', marginBottom: 12 }}>
                <JourneyRouteMap stops={routeMap.routeStops} preview={mapPreview} height={240} />
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <JourneyDates
                departureDate={journey.departureDate}
                returnDate={journey.returnDate}
                durationPreset={journey.tripDurationPreset}
                onSave={handleSaveDates}
                busy={mutating}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <InfoPills
                stops={journey.stops || []}
                legs={journey.legs || []}
                costEstimating={pricingPending}
                onSearchNightTrains={openNightTrainSearch}
              />
            </div>

            {Array.isArray(journey.tips) && journey.tips.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <JourneyTips tips={journey.tips} />
              </div>
            )}

            <div style={{ background: '#ffffff', borderRadius: 16, padding: 16, marginBottom: 16 }}>
              <RouteTimeline
                nightsPending={nightsPending}
                stops={journey.stops || []}
                legs={journey.legs || []}
                departureDate={journey.departureDate}
                returnDate={journey.returnDate}
                stickyTop={APP_HEADER_HEIGHT + headerH}
                busy={mutating}
                onAddStop={handleAddStop}
                onRemoveStop={handleRemoveStop}
                replaceRequestStopId={replaceRequestStopId}
                onReplaceRequestHandled={() => setReplaceRequestStopId(null)}
                onReplaceStop={handleReplaceStop}
                onSuggestPlaces={handleSuggestPlaces}
                onPreviewPlace={handlePreviewPlace}
                onOpenLeg={handleOpenLeg}
                onOpenStop={handleOpenStop}
                onBookingStop={handleBookingStop}
                bookingBusyStopId={bookingBusyStopId}
                onNightsChange={handleStopNightsChange}
                onNightsSaved={handleStopNightsSaved}
                onSearchNightTrainsNearby={setNearbyLegId}
                onToggleStopPlanned={handleToggleStopPlanned}
                onToggleLegPlanned={handleToggleLegPlanned}
                onOmioLeg={handleOmioLeg}
                onNightTrainBooking={handleNightTrainBooking}
                onChangeLegMode={handleChangeLegMode}
                omioBusyLegId={omioBusyLegId}
                {...routeMap.timelineProps}
              />
            </div>

            <ApproxNote />
          </>
        )}
      </DetailSplitLayout>
      <LegSheet
        journeyId={journey?.id || null}
        open={!!openLeg}
        leg={openLeg}
        from={openLegStops.from}
        to={openLegStops.to}
        route={openLeg ? legRoute(openLeg.id) : null}
        busy={mutating}
        expectedDate={openLegExpectedDate}
        changeAtFrom={Boolean(openLeg && changeIds.has(openLeg.fromStopId))}
        changeAtTo={Boolean(openLeg && changeIds.has(openLeg.toStopId))}
        onSave={handleSaveLeg}
        onClose={() => setOpenLegId(null)}
        onShiftNightTrainDate={handleShiftNightTrainDate}
        onSearchNightTrainsNearby={setNearbyLegId}
      />
      <NightTrainsNearbyDialog
        open={!!nearby}
        journeyId={journey?.id || null}
        legId={nearby?.legId ?? null}
        legs={nearbyLegs}
        onPickLeg={setNearbyLegId}
        onAdd={handleAddNightTrainNearby}
        onClose={() => setNearby(null)}
      />
      <StopSheet
        journeyId={journey?.id || null}
        nightsPending={nightsPending}
        open={!!openStop}
        stop={openStop}
        busy={mutating}
        checkin={openStopWindow.checkin}
        checkout={openStopWindow.checkout}
        dateLabel={openStopWindow.label}
        onNightsChange={handleStopNightsChange}
        onNightsSaved={handleStopNightsSaved}
        onSave={handleSaveStop}
        onReplace={(stopId) => { setOpenStopId(null); setReplaceRequestStopId(stopId); }}
        onDelete={(stopId) => { setOpenStopId(null); handleRemoveStop(stopId); }}
        onClose={() => setOpenStopId(null)}
      />
      {shareOpen && journey?.tripId && (
        <TripShareDialog
          tripId={journey.tripId}
          journeyId={journey.id}
          tripTitle={journey.tripTitle || journey.title || 'Trip'}
          source="journey_detail"
          onClose={() => setShareOpen(false)}
          onVisibilityChange={(visibility) => setJourney((j: any) => (j ? { ...j, tripShareVisibility: visibility } : j))}
        />
      )}

      {copying && journey && (
        <NameDialog
          title="Create a copy"
          label="Name for the copy"
          initialValue={`Copy of ${journey.title || 'route'}`}
          confirmText="Create copy"
          busy={copyBusy}
          onConfirm={handleCopy}
          onCancel={() => setCopying(false)}
        />
      )}

      {renaming && journey && (
        <NameDialog
          title="Rename journey"
          label="Journey name"
          initialValue={journey.title || ''}
          busy={savingName}
          onConfirm={handleRename}
          onCancel={() => setRenaming(false)}
        />
      )}

      {confirm && (
        <ConfirmationDialog
          title={confirm.title}
          message={confirm.message}
          confirmText={confirm.confirmText}
          confirmButtonStyle="danger"
          onConfirm={() => { const run = confirm.onConfirm; setConfirm(null); run(); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

export default JourneyDetailPage;
